import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { useYearParam, YearControl } from '@/features/clients/components/YearControl'
import { useCounterparty } from '@/features/counterparties/api/useCounterparties'
import { useCounterpartyStatement } from '@/features/counterparties/api/useCounterpartyMoney'
import { CounterpartyMoneyTiles } from '@/features/counterparties/components/CounterpartyMoneyTiles'
import {
  CounterpartyDetails,
  CounterpartyFacts,
} from '@/features/counterparties/components/CounterpartyProfile'
import { CounterpartyRecordHeader } from '@/features/counterparties/components/CounterpartyRecordHeader'
import { CounterpartyStatementTable } from '@/features/counterparties/components/CounterpartyStatementTable'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import type { CounterpartyStatement } from '@/types/domain'

/* One counterparty (S-22b, S-22e — `direction-ac2-final.png` with the owner's
 * 2026-10-23 changes): the header with «Год», the strip of facts ending in
 * «Средняя ставка», four money tiles, «Выписка» — one row per work with its
 * expansion — and «Реквизиты и контакты».
 *
 * The year is the client statement's (`useYearParam`: in the URL, the current
 * year by default). The money follows the server's `moneyUnavailable` the way
 * the client page does: `forbidden` — the reader's role does not reach the
 * money of works — shows no tiles and no rows; `unavailable` — a read failed —
 * a banner over the works with no figures. Every figure is served.
 *
 * A reader without `counterparty.read` is told so instead of being sent a read
 * the server answers 404 — the sidebar offers them no way here, so an old or
 * typed link is the only one. A 404 for a reader who may read covers a missing
 * record, and ErrorState words it. */
export function CounterpartyPage() {
  const { t } = useTranslation()
  const { counterpartyId = '' } = useParams()
  const { data: session, isPending: sessionPending } = useSession()
  const rights = counterpartyRights(session)
  const [year, setYear] = useYearParam()
  const { data, isPending, isError, error, refetch } = useCounterparty(counterpartyId, {
    enabled: rights.read,
  })
  const statement = useCounterpartyStatement(counterpartyId, year, { enabled: rights.read })

  if (!sessionPending && !rights.read) {
    return (
      <Card>
        <EmptyState
          title={t('counterparties.forbidden.title')}
          description={t('counterparties.forbidden.description')}
        />
      </Card>
    )
  }
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-48 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  const years = statement.data?.years ?? []

  return (
    <>
      <CounterpartyRecordHeader
        record={data}
        controls={
          /* No year to choose with no work — and none for a reader whose
             money is forbidden, who is served `years: []`. */
          years.length > 0 ? (
            <YearControl
              years={years}
              value={year}
              onChange={setYear}
              label={t('counterparties.statement.year')}
            />
          ) : undefined
        }
      />
      <CounterpartyFacts record={data} averageRate={statement.data?.totals?.averageRate ?? null} />
      {statement.isError ? (
        <Card>
          <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />
        </Card>
      ) : statement.isPending ? (
        <Skeleton className="h-64 w-full rounded-[var(--radius-DEFAULT)]" />
      ) : (
        <StatementSection statement={statement.data} />
      )}
      <CounterpartyDetails record={data} />
    </>
  )
}

/** The tiles and «Выписка», in the state the server answered. */
function StatementSection({ statement }: { statement: CounterpartyStatement }) {
  const { t } = useTranslation()

  if (statement.moneyUnavailable === 'forbidden') {
    return (
      <Card>
        <EmptyState
          title={t('counterparties.money.forbidden.title')}
          description={t('counterparties.money.forbidden.description')}
        />
      </Card>
    )
  }

  const hasWorks = statement.years.length > 0 || statement.rows.length > 0
  /* With the money unavailable the server knows only the carried works, so an
     empty statement then cannot say there is no work at all. */
  const empty = hasWorks || statement.moneyUnavailable === 'unavailable' ? 'emptyYear' : 'empty'

  return (
    <>
      {statement.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('counterparties.money.unavailable.title')}>
          {t('counterparties.money.unavailable.body')}
        </Banner>
      ) : null}

      {statement.totals && hasWorks ? (
        <CounterpartyMoneyTiles totals={statement.totals} scoped={statement.scoped} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('counterparties.statement.title')}</CardTitle>
        </CardHeader>
        {statement.rows.length === 0 ? (
          <EmptyState
            title={t(`counterparties.statement.${empty}.title`)}
            description={t(`counterparties.statement.${empty}.description`)}
          />
        ) : (
          <CounterpartyStatementTable
            rows={statement.rows}
            opening={statement.opening?.balance}
            totals={statement.totals}
          />
        )}
      </Card>
    </>
  )
}
